/**
 * CMS Podcast Episode Query
 *
 * Server-side query for fetching podcast episode data.
 */

import { query } from "@solidjs/router";
import type { CMSPodcastEpisode, CMSPodcastEpisodeResponse } from "~/types/cms";
import { env } from "~/config/env";
import { isPreviewTokenValid } from "~/utils/previewToken.server";
import { buildPodcastEpisodeBySlugUrl } from "../queryUrls";
import { logger } from "~/lib/logger/logger";
import { fetchFromCMS, findByTenant } from "../queryUtils";

/**
 * Server-side query for fetching a podcast episode by slug
 *
 * @param slug - The episode slug (e.g., 'episode-1')
 * @param tenantSlug - The tenant slug for multi-tenancy filtering
 * @param previewToken - CMS-signed preview token; drafts are read only when it verifies server-side for the tenant
 * @returns Episode data or null if not found
 */
export const getPodcastEpisodeBySlugQuery = query(
  async (slug: string, tenantSlug: string, previewToken?: string) => {
    "use server";
    const draft = isPreviewTokenValid(previewToken, tenantSlug);

    const LOG_PREFIX = "[podcastEpisodeBySlug]";

    const url = `${env.cmsApiUrl}${buildPodcastEpisodeBySlugUrl({ slug, tenantSlug, draft })}`;

    const data = await fetchFromCMS<CMSPodcastEpisodeResponse>(url, {
      logPrefix: LOG_PREFIX,
      previewToken: draft ? previewToken : undefined,
    });

    if (!data?.docs || data.docs.length === 0) {
      logger.warn(`${LOG_PREFIX} No episode found with slug: ${slug}`);
      return null;
    }

    // Payload ignores nested tenant[slug] filters — filter client-side
    const episode = findByTenant(data.docs, tenantSlug);

    if (!episode) {
      logger.warn(`${LOG_PREFIX} No episode found for tenant: ${tenantSlug}`);
      return null;
    }

    logger.info(`${LOG_PREFIX} Found episode: ${episode.title}`);
    return episode as CMSPodcastEpisode;
  },
  "podcastEpisodeBySlug"
);
